import { useCallback, useRef } from 'react'
import { v4 as uuid } from 'uuid'
import { uploadToCloudinary, getMediaType } from '@/lib/cloudinary'
import { saveMediaItemWithSession, updateMediaAI } from '@/lib/supabase'
import { extractMediaMetadata } from '@/lib/claude'
import { queueAdd, queueUpdate, queueRemove } from '@/lib/uploadQueue'
import { useUploadStore } from '@/stores/uploadStore'
import { useSessionStore } from '@/stores/sessionStore'
import { useAuthStore } from '@/stores/authStore'
import { captureError } from '@/lib/sentry'
import type { UploadQueueItem } from '@/types'

const MAX_RETRIES = 3
const RETRY_DELAY = 2000

export function useUploadQueue() {
  const { addItem, updateItem, removeItem, items, activeCount, errorCount } = useUploadStore()
  const { session, incrementCount } = useSessionStore()
  const { participant } = useAuthStore()
  const abortRefs = useRef<Map<string, AbortController>>(new Map())

  const enqueue = useCallback(async (
    file: File,
    participantId?: string,
    eventId?: string,
  ): Promise<string> => {
    const id = uuid()
    const item: UploadQueueItem = {
      id, file, participantId, eventId,
      status: 'queued', progress: 0, retries: 0, createdAt: Date.now(),
    }
    await queueAdd(item)
    addItem(item)
    processItem(item)
    return id
  }, [addItem])

  const processItem = useCallback(async (item: UploadQueueItem) => {
    const { id, file, participantId, eventId } = item
    const abort = new AbortController()
    abortRefs.current.set(id, abort)

    const patch = (p: Partial<UploadQueueItem>) => {
      updateItem(id, p)
      queueUpdate(id, p)
    }

    for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
      try {
        // ── 1. Upload to Cloudinary ──────────────────────────────────────────
        patch({ status: 'uploading', progress: 0 })
        const cloud = await uploadToCloudinary(
          file,
          (pct) => patch({ progress: pct }),
          abort.signal,
        )

        // ── 2. Save to Supabase — session-aware, held if anonymous ───────────
        patch({ status: 'saving', progress: 100 })
        const saved = await saveMediaItemWithSession({
          cloudinary_url:   cloud.secure_url,
          cloudinary_id:    cloud.public_id,
          media_type:       getMediaType(file),
          file_name:        file.name,
          file_size:        file.size,
          participant_id:   participantId,
          event_id:         eventId ?? session?.eventId ?? undefined,
          shoot_session_id: session?.id,
          is_anonymous:     participant?.is_anonymous,
          contact_captured: participant?.contact_captured,
        })

        // Increment local session count
        incrementCount()

        // ── 3. Claude AI evaluation ──────────────────────────────────────────
        patch({ status: 'routing' })
        const ai = await extractMediaMetadata({
          cloudinary_url: cloud.secure_url,
          media_type:     getMediaType(file),
          file_name:      file.name,
        })

        // ── 4. Update Supabase with full AI evaluation ───────────────────────
        // Note: if status is 'held', updateMediaAI sets it to 'processed'
        // but push engine checks for held and won't route held items.
        // The held flag lives on status — once released it becomes 'pending'
        // and the push engine runs from UploadPage.handleDone as normal.
        await updateMediaAI(saved.id, {
          tags:              ai.tags,
          description:       ai.description,
          mood:              ai.mood,
          route_score:       ai.route_score,
          grade:             ai.grade,
          editor_note:       ai.editor_note,
          flags:             ai.flags,
          hold_for_review:   ai.hold_for_review,
          technical_scores:  ai.technical_scores,
          editorial_scores:  ai.editorial_scores,
          commercial_scores: ai.commercial_scores,
          avg_technical:     ai.avg_technical,
          avg_editorial:     ai.avg_editorial,
          avg_commercial:    ai.avg_commercial,
          suggested_audiences: ai.suggested_audiences,
        })

        patch({
          status: 'done',
          result: {
            mediaItemId:   saved.id,
            cloudinaryUrl: cloud.secure_url,
            aiTags:        ai.tags,
            aiDescription: ai.description,
            aiMood:        ai.mood,
            routeScore:    ai.route_score,
            grade:         ai.grade,
            editorNote:    ai.editor_note,
          },
        })
        abortRefs.current.delete(id)
        return

      } catch (err: unknown) {
        if ((err as Error).message === 'Upload cancelled') {
          patch({ status: 'error', error: 'Cancelled' })
          abortRefs.current.delete(id)
          return
        }

        captureError(err, { uploadId: id, attempt, fileName: file.name })

        if (attempt < MAX_RETRIES) {
          patch({ status: 'retrying', retries: attempt })
          await sleep(RETRY_DELAY * attempt)
        } else {
          patch({ status: 'error', error: (err as Error).message ?? 'Upload failed' })
          abortRefs.current.delete(id)
        }
      }
    }
  }, [updateItem, session, participant, incrementCount])

  const cancel = useCallback((id: string) => {
    abortRefs.current.get(id)?.abort()
    abortRefs.current.delete(id)
  }, [])

  const retry = useCallback((item: UploadQueueItem) => {
    updateItem(item.id, { status: 'queued', retries: 0, error: undefined })
    processItem({ ...item, status: 'queued', retries: 0 })
  }, [updateItem, processItem])

  const dismiss = useCallback(async (id: string) => {
    await queueRemove(id)
    removeItem(id)
  }, [removeItem])

  return { enqueue, cancel, retry, dismiss, items, activeCount, errorCount }
}

const sleep = (ms: number) => new Promise(r => setTimeout(r, ms))
